import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { IngestFile, IngestRow, IngestionService } from '../../../../../js/api';
import { useDocumentTitle } from '../../../../../js/app/hooks/use-document-title';
import { handleApiError } from '../../../../../js/app/utils/errorhandling';
import { Badge } from '../../../components/badge';
import { Button } from '../../../components/button';
import { Checkbox } from '../../../components/checkbox';
import { Dialog, DialogActions, DialogBody, DialogTitle } from '../../../components/dialog';
import { Heading } from '../../../components/heading';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/table';
import { Textarea } from '../../../components/textarea';
import { StatusCodes } from '../../../constants/workflows';
import { useCodeDefinitionsAsMaps } from '../../../hooks/useCodeDefinitions';
import { useHasPermission } from '../../../hooks/useHasPermission';
import { useInfiniteScroll } from '../../../hooks/useInfiniteScroll';
import { formatDate, formatTimestamp } from '../../../utils/date';
import {
  getIngestFileStatusBadgeColor,
  getIngestRowOutcomeBadgeColor,
  READ_ONLY_LABEL_STYLE,
  STICKY_HEADER,
} from '../../../utils/globalStyles';
import { MAIN_CONTENT_AREA, PAGE_HEADER } from '../../../utils/layoutStyles';
import {
  INGEST_FILE_UPDATE_PERMISSION,
  INGEST_ROW_UPDATE_PERMISSION,
} from '../../../utils/permissions';
import { STAFF_INGEST_FILES_QUERY_KEY, STAFF_INGEST_ROWS_QUERY_KEY } from './queryKeys';

const PAGE_SIZE = 50;

const REPROCESSABLE_STATUSES: string[] = [StatusCodes.PROC_WITH_ERRORS, StatusCodes.FAILED];
const ABANDONABLE_STATUSES: string[] = [
  StatusCodes.PENDING,
  StatusCodes.PROC_WITH_ERRORS,
  StatusCodes.FAILED,
];
const FINAL_STATUSES: string[] = [StatusCodes.PROCESSED, StatusCodes.NOT_LOADED];

// Error rows staff can still act on: open, or already staged for retry
// (which can be re-marked or dismissed until the file reprocesses).
const isActionableError = (row: IngestRow): boolean =>
  row.outcome === 'error' && (row.resolution === 'open' || row.resolution === 'retry');

/** Pull a display value out of the canonical (client-agnostic) row data. */
const normValue = (row: IngestRow, field: string): string => {
  const value = (row.normalized_data as Record<string, unknown> | null)?.[field];
  return value === null || value === undefined ? '' : String(value);
};

const rowProviderName = (row: IngestRow): string => {
  if (row.provider) {
    return [row.provider.first_name, row.provider.last_name].filter(Boolean).join(' ');
  }
  const rawName = [normValue(row, 'provider_first_name'), normValue(row, 'provider_last_name')]
    .filter(Boolean)
    .join(' ');
  return rawName || '—';
};

const rowShiftDate = (row: IngestRow): string => {
  if (row.shift_start_on) return formatDate(row.shift_start_on);
  // Unmatched (usually errored) rows render whatever the file said — which
  // may be an ISO datetime or an arbitrary unparsed string. formatDate falls
  // back to the raw string for anything unparseable.
  const raw = normValue(row, 'schedule_start_on');
  return raw ? formatDate(raw.split('T')[0]) : '—';
};

/** The HH:mm of a normalized time value, tolerating full ISO datetimes and
 * falling back to the raw string for anything else. */
const rowTimePart = (value: string): string => {
  const timeish = value.includes('T') ? value.split('T')[1] : value;
  return /^\d{2}:\d{2}/.test(timeish) ? timeish.slice(0, 5) : value;
};

const rowShiftTimes = (row: IngestRow): string => {
  const start = normValue(row, 'schedule_start_time');
  const end = normValue(row, 'schedule_end_time');
  if (!start && !end) return '';
  return `${rowTimePart(start)}–${rowTimePart(end)}`;
};

const CountChip: React.FC<{ label: string; value: number; alert?: boolean }> = ({
  label,
  value,
  alert = false,
}) => (
  <div className="tw:flex tw:flex-col tw:items-center tw:rounded-lg tw:border tw:border-zinc-200 tw:bg-white tw:px-3 tw:py-1.5">
    <span className={READ_ONLY_LABEL_STYLE}>{label}</span>
    <span
      className={
        alert && value > 0
          ? 'tw:font-semibold tw:text-red-600'
          : 'tw:font-semibold tw:text-zinc-800'
      }
    >
      {value}
    </span>
  </div>
);

const BulkActionBar: React.FC<{
  selectedCount: number;
  allOpenSelected: boolean;
  allLoadedSelected: boolean;
  openErrorCount: number;
  busy: boolean;
  retrying: boolean;
  dismissing: boolean;
  onSelectAllOpen: () => void;
  onRetry: () => void;
  onDismiss: () => void;
  onClear: () => void;
}> = ({
  selectedCount,
  allOpenSelected,
  allLoadedSelected,
  openErrorCount,
  busy,
  retrying,
  dismissing,
  onSelectAllOpen,
  onRetry,
  onDismiss,
  onClear,
}) => {
  if (selectedCount === 0 && !allOpenSelected) {
    return null;
  }
  return (
    <div
      className="tw:mt-2 tw:flex tw:flex-wrap tw:items-center tw:gap-2 tw:rounded-lg tw:border tw:border-brand-green-1 tw:bg-brand-green-3 tw:px-3 tw:py-2"
      data-testid="ingest-bulk-bar"
    >
      <span className="tw:text-sm tw:font-medium">
        {allOpenSelected
          ? `All ${openErrorCount} open errors selected`
          : `${selectedCount} selected`}
      </span>
      {!allOpenSelected && allLoadedSelected && openErrorCount > selectedCount && (
        <Button plain small onClick={onSelectAllOpen} data-testid="ingest-bulk-select-all-open">
          Select all {openErrorCount} open errors
        </Button>
      )}
      <div className="tw:ml-auto tw:flex tw:items-center tw:gap-2">
        <span className="tw:text-xs tw:text-zinc-500">
          Marking only — Reprocess runs the rows marked for retry.
        </span>
        <Button
          small
          color="brandgreen1"
          disabled={busy}
          onClick={onRetry}
          data-testid="ingest-bulk-retry"
        >
          {retrying ? 'Marking...' : 'Mark for Retry'}
        </Button>
        <Button
          small
          color="brand-green-outline"
          disabled={busy}
          onClick={onDismiss}
          data-testid="ingest-bulk-dismiss"
        >
          {dismissing ? 'Dismissing...' : 'Dismiss'}
        </Button>
        <Button plain small disabled={busy} onClick={onClear}>
          Clear
        </Button>
      </div>
    </div>
  );
};

const FileHeaderActions: React.FC<{
  statusCode: string;
  canReprocess: boolean;
  reprocessPending: boolean;
  onReprocess: () => void;
  onMarkNotLoaded: () => void;
}> = ({ statusCode, canReprocess, reprocessPending, onReprocess, onMarkNotLoaded }) => (
  <div className="tw:flex tw:gap-2">
    {REPROCESSABLE_STATUSES.includes(statusCode) && (
      <Button
        color="brandgreen1"
        onClick={onReprocess}
        disabled={reprocessPending || !canReprocess}
        title={canReprocess ? undefined : 'Mark error rows for retry first'}
        data-testid="ingest-reprocess-button"
      >
        {reprocessPending ? 'Starting...' : 'Reprocess'}
      </Button>
    )}
    {ABANDONABLE_STATUSES.includes(statusCode) && (
      <Button
        color="stoplight-danger-outline"
        onClick={onMarkNotLoaded}
        data-testid="ingest-not-loaded-button"
      >
        Mark Not Loaded
      </Button>
    )}
  </div>
);

const MarkNotLoadedDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  ingestFile: IngestFile;
  onChanged: () => void;
}> = ({ open, onClose, ingestFile, onChanged }) => {
  const [comment, setComment] = useState('');
  const mutation = useMutation({
    mutationFn: () =>
      IngestionService.ingestionFilesStatusUpdatePartialUpdate({
        id: ingestFile.id,
        requestBody: { code: StatusCodes.NOT_LOADED, comment },
      }),
    onSuccess: () => {
      toast.success('File marked as not loaded');
      setComment('');
      onChanged();
      onClose();
    },
    onError: (error: Error) => handleApiError(error, { messagePrefix: 'Update failed' }),
  });

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Mark Not Loaded</DialogTitle>
      <DialogBody>
        <p className="tw:text-zinc-700">
          This abandons the file: remaining open errors stay as a historical record and can no
          longer be retried or dismissed. Explain why this file will not be loaded.
        </p>
        <Textarea
          className="tw:mt-3"
          rows={3}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="Reason (required)"
          data-testid="ingest-not-loaded-comment"
        />
      </DialogBody>
      <DialogActions>
        <Button plain onClick={onClose} disabled={mutation.isPending}>
          Cancel
        </Button>
        <Button
          color="stoplight-danger"
          onClick={() => mutation.mutate()}
          disabled={!comment.trim() || mutation.isPending}
          data-testid="ingest-not-loaded-confirm"
        >
          {mutation.isPending ? 'Saving...' : 'Mark Not Loaded'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/** The row exactly as the customer's file said it — prettified, for support
 * archaeology ("what did they actually send us?"). Read-only by design: fixes
 * happen via retry/dismiss and reprocess, never by editing the record. */
const RawDataDialog: React.FC<{ row: IngestRow | null; onClose: () => void }> = ({
  row,
  onClose,
}) => {
  const prettyJson = row ? JSON.stringify(row.raw_data, null, 2) : '';
  return (
    <Dialog open={!!row} onClose={onClose} size="2xl">
      <DialogTitle>Raw data — row {row?.row_number ?? '?'}</DialogTitle>
      <DialogBody>
        <p className="tw:text-sm tw:text-zinc-600">
          Exactly as received from the file, before normalization.
        </p>
        <pre
          className="tw:mt-3 tw:max-h-[60vh] tw:overflow-auto tw:rounded-lg tw:bg-zinc-900 tw:px-4 tw:py-3 tw:text-xs tw:leading-5 tw:text-zinc-100"
          data-testid="ingest-raw-json"
        >
          {prettyJson}
        </pre>
      </DialogBody>
      <DialogActions>
        <Button
          plain
          onClick={async () => {
            await navigator.clipboard.writeText(prettyJson);
            toast.success('JSON copied');
          }}
          data-testid="ingest-raw-copy"
        >
          Copy JSON
        </Button>
        <Button color="brandgreen1" onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const StaffIngestFileDetail: React.FC = () => {
  const canUpdateFile = useHasPermission(INGEST_FILE_UPDATE_PERMISSION);
  const canUpdateRows = useHasPermission(INGEST_ROW_UPDATE_PERMISSION);
  const { ingestFileId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [errorsOnly, setErrorsOnly] = useState(true);
  const [showNotLoadedDialog, setShowNotLoadedDialog] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [rawDataRow, setRawDataRow] = useState<IngestRow | null>(null);
  // "All open errors" mode covers error rows beyond the loaded pages; bulk
  // calls then target the file instead of explicit ids.
  const [allOpenSelected, setAllOpenSelected] = useState(false);

  const { data: ingestFile, isLoading } = useQuery({
    queryKey: [STAFF_INGEST_FILES_QUERY_KEY, ingestFileId],
    queryFn: () => IngestionService.ingestionFilesRetrieve({ id: ingestFileId as string }),
    enabled: !!ingestFileId,
    refetchInterval: (data) => {
      // Poll while the file is being processed so the page updates live.
      const status = data?.current_status.code;
      return status === StatusCodes.PROCESSING ||
        status === StatusCodes.REPROCESSING ||
        status === StatusCodes.UPLOADED
        ? 4000
        : false;
    },
  });

  useDocumentTitle(ingestFile ? `Import: ${ingestFile.original_filename}` : 'Import');

  const rowsQueryKey = useMemo(
    () => [STAFF_INGEST_ROWS_QUERY_KEY, ingestFileId, errorsOnly],
    [ingestFileId, errorsOnly]
  );

  const {
    data: rowsData,
    isLoading: rowsLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteQuery({
    queryKey: rowsQueryKey,
    queryFn: async ({ pageParam = 0 }) =>
      IngestionService.ingestionRowsList({
        ingestFile: ingestFileId,
        outcome: errorsOnly ? 'error' : undefined,
        limit: PAGE_SIZE,
        offset: pageParam as number,
      }),
    enabled: !!ingestFileId,
    getNextPageParam: (lastPage, allPages) =>
      lastPage.next ? allPages.length * PAGE_SIZE : undefined,
  });

  const rows = useMemo(
    () => rowsData?.pages.flatMap((page) => page.results || []) || [],
    [rowsData]
  );
  const totalRowCount = rowsData?.pages[0]?.count || 0;

  const loadMore = useCallback(async () => {
    if (hasNextPage && !isFetchingNextPage) {
      await fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const { loadingRef } = useInfiniteScroll(loadMore, hasNextPage || false, {
    enabled: hasNextPage && !rowsLoading,
  });

  const { data: labelMaps } = useCodeDefinitionsAsMaps([
    'IngestReportType',
    'IngestRowOutcome',
    'IngestRowErrorCode',
    'IngestRowResolution',
  ]);

  const invalidate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: [STAFF_INGEST_FILES_QUERY_KEY] });
    queryClient.invalidateQueries({ queryKey: [STAFF_INGEST_ROWS_QUERY_KEY, ingestFileId] });
  }, [queryClient, ingestFileId]);

  const selectableRows = useMemo(() => rows.filter(isActionableError), [rows]);
  const allLoadedSelected =
    selectableRows.length > 0 && selectableRows.every((row) => selectedIds.has(row.id));

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
    setAllOpenSelected(false);
  }, []);

  // When polling sees the file change status (processing finished, reprocess
  // kicked off, ...) the row results are stale — refresh them and drop any
  // selection that may point at swept rows.
  const currentStatusCode = ingestFile?.current_status.code;
  useEffect(() => {
    if (currentStatusCode) {
      queryClient.invalidateQueries({ queryKey: [STAFF_INGEST_ROWS_QUERY_KEY, ingestFileId] });
      clearSelection();
    }
  }, [currentStatusCode, queryClient, ingestFileId, clearSelection]);

  const toggleRow = useCallback(
    (row: IngestRow) => {
      setSelectedIds((prev) => {
        // Leaving "all open errors" mode pins the selection to the loaded rows.
        const next = new Set(allOpenSelected ? selectableRows.map((r) => r.id) : prev);
        if (next.has(row.id)) {
          next.delete(row.id);
        } else {
          next.add(row.id);
        }
        return next;
      });
      setAllOpenSelected(false);
    },
    [allOpenSelected, selectableRows]
  );

  const toggleAllLoaded = useCallback(() => {
    if (allLoadedSelected || allOpenSelected) {
      clearSelection();
    } else {
      setSelectedIds(new Set(selectableRows.map((row) => row.id)));
    }
  }, [allLoadedSelected, allOpenSelected, clearSelection, selectableRows]);

  const bulkPayload = useCallback(
    () =>
      allOpenSelected ? { ingest_file: ingestFileId as string } : { ids: Array.from(selectedIds) },
    [allOpenSelected, ingestFileId, selectedIds]
  );

  const bulkDismissMutation = useMutation({
    mutationFn: () =>
      IngestionService.ingestionRowsBulkDismissCreate({ requestBody: bulkPayload() }),
    onSuccess: (result) => {
      toast.success(`${result.processed} rows dismissed`);
      clearSelection();
      invalidate();
    },
    onError: (error: Error) => handleApiError(error, { messagePrefix: 'Bulk dismiss failed' }),
  });

  const bulkRetryMutation = useMutation({
    mutationFn: () => IngestionService.ingestionRowsBulkRetryCreate({ requestBody: bulkPayload() }),
    onSuccess: (result) => {
      toast.success(`${result.processed} rows marked for retry`);
      clearSelection();
      invalidate();
    },
    onError: (error: Error) => handleApiError(error, { messagePrefix: 'Bulk retry failed' }),
  });

  const reprocessMutation = useMutation({
    mutationFn: () =>
      IngestionService.ingestionFilesStatusUpdatePartialUpdate({
        id: ingestFileId as string,
        requestBody: { code: StatusCodes.REPROCESSING },
      }),
    onSuccess: () => {
      toast.success('Reprocessing started');
      invalidate();
    },
    onError: (error: Error) => handleApiError(error, { messagePrefix: 'Reprocess failed' }),
  });

  if (isLoading || !ingestFile) {
    return (
      <main className={MAIN_CONTENT_AREA}>
        <div className="tw:text-center tw:py-8">Loading import file...</div>
      </main>
    );
  }

  const statusCode = ingestFile.current_status.code;
  const frozen = FINAL_STATUSES.includes(statusCode);
  const actionableErrorCount = ingestFile.rows_error + ingestFile.rows_retry;
  // Reprocess only re-runs the rows staged for retry, so it needs at least
  // one — except a FAILED file, where it re-runs the whole pipeline.
  const canReprocess = statusCode === StatusCodes.FAILED || ingestFile.rows_retry > 0;

  return (
    <main className={MAIN_CONTENT_AREA} data-testid="ingest-file-detail-page">
      <div className={STICKY_HEADER}>
        <div className={PAGE_HEADER}>
          <div className="tw:flex tw:items-center tw:gap-3 tw:min-w-0">
            <Button plain onClick={() => navigate('/staff/ingest')} title="Back to Imports">
              <i className="far fa-fw fa-arrow-left" />
            </Button>
            <Heading level={1} className="tw:!text-brand-green-0 tw:truncate">
              <i className="far fa-fw fa-file-import tw:mr-1" />
              {ingestFile.original_filename}
            </Heading>
            <Badge color={getIngestFileStatusBadgeColor(statusCode)}>
              {ingestFile.current_status.title}
            </Badge>
          </div>
          {canUpdateFile && (
            <FileHeaderActions
              statusCode={statusCode}
              canReprocess={canReprocess}
              reprocessPending={reprocessMutation.isPending}
              onReprocess={() => reprocessMutation.mutate()}
              onMarkNotLoaded={() => setShowNotLoadedDialog(true)}
            />
          )}
        </div>

        <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-x-6 tw:gap-y-1 tw:text-sm tw:text-zinc-600">
          <span>
            <span className={READ_ONLY_LABEL_STYLE}>Client&nbsp;</span>
            {ingestFile.healthcare_entity_name}
          </span>
          <span>
            <span className={READ_ONLY_LABEL_STYLE}>Report&nbsp;</span>
            {labelMaps?.IngestReportType?.[ingestFile.report_type] || ingestFile.report_type}
          </span>
          <span>
            <span className={READ_ONLY_LABEL_STYLE}>Uploaded&nbsp;</span>
            {formatTimestamp(ingestFile.created_at)}
            {ingestFile.created_by ? ` by ${ingestFile.created_by}` : ''}
          </span>
          {ingestFile.processed_at && (
            <span>
              <span className={READ_ONLY_LABEL_STYLE}>Processed&nbsp;</span>
              {formatTimestamp(ingestFile.processed_at)}
            </span>
          )}
        </div>

        {ingestFile.failure_message && (
          <div className="tw:mt-3 tw:rounded-lg tw:border tw:border-red-200 tw:bg-red-50 tw:px-4 tw:py-3 tw:text-red-700">
            <p className="tw:font-semibold">File failed to process</p>
            <p className="tw:text-sm">{ingestFile.failure_message}</p>
          </div>
        )}

        {ingestFile.duplicate_of_id && (
          <div className="tw:mt-3 tw:rounded-lg tw:border tw:border-amber-200 tw:bg-amber-50 tw:px-4 tw:py-3 tw:text-amber-800 tw:text-sm">
            This file is identical to an{' '}
            <Link
              className="tw:underline"
              to={`/staff/ingest/${ingestFile.duplicate_of_id}`}
              data-testid="ingest-duplicate-link"
            >
              earlier upload
            </Link>
            .
          </div>
        )}

        <div className="tw:mt-3 tw:flex tw:flex-wrap tw:gap-2">
          <CountChip label="Rows" value={ingestFile.rows_total} />
          <CountChip label="Created" value={ingestFile.rows_created} />
          <CountChip label="Updated" value={ingestFile.rows_updated} />
          <CountChip label="Unchanged" value={ingestFile.rows_unchanged} />
          <CountChip label="Duplicates" value={ingestFile.rows_duplicate} />
          <CountChip label="Skipped" value={ingestFile.rows_skipped} />
          <CountChip label="Open Errors" value={ingestFile.rows_error} alert />
          <CountChip label="For Retry" value={ingestFile.rows_retry} />
        </div>

        <div className="tw:mt-3 tw:flex tw:items-center tw:gap-2">
          <Button
            small
            color={errorsOnly ? 'brandgreen1' : 'zinc-outline'}
            onClick={() => setErrorsOnly(true)}
            data-testid="ingest-rows-filter-errors"
          >
            Errors
          </Button>
          <Button
            small
            color={!errorsOnly ? 'brandgreen1' : 'zinc-outline'}
            onClick={() => setErrorsOnly(false)}
            data-testid="ingest-rows-filter-all"
          >
            All Rows
          </Button>
          <span className="tw:text-xs tw:text-zinc-500">
            Showing {rows.length} of {totalRowCount}
          </span>
        </div>

        {canUpdateRows && (
          <BulkActionBar
            selectedCount={selectedIds.size}
            allOpenSelected={allOpenSelected}
            allLoadedSelected={allLoadedSelected}
            openErrorCount={actionableErrorCount}
            busy={bulkDismissMutation.isPending || bulkRetryMutation.isPending}
            retrying={bulkRetryMutation.isPending}
            dismissing={bulkDismissMutation.isPending}
            onSelectAllOpen={() => setAllOpenSelected(true)}
            onRetry={() => bulkRetryMutation.mutate()}
            onDismiss={() => bulkDismissMutation.mutate()}
            onClear={clearSelection}
          />
        )}
      </div>

      <div className="tw:mt-3">
        {rowsLoading && rows.length === 0 && (
          <div className="tw:text-center tw:py-8">Loading rows...</div>
        )}
        {!rowsLoading && rows.length === 0 ? (
          <div data-testid="ingest-rows-empty" className="tw:text-center tw:text-zinc-500 tw:py-8">
            {errorsOnly ? 'No error rows — nothing needs attention.' : 'No rows recorded.'}
          </div>
        ) : (
          <>
            <Table striped dense>
              <TableHead>
                <TableHeader className="tw:w-8">
                  {!frozen && selectableRows.length > 0 && (
                    <Checkbox
                      color="brandgreen"
                      checked={allOpenSelected || allLoadedSelected}
                      onChange={toggleAllLoaded}
                      aria-label="Select all open error rows"
                      data-testid="ingest-rows-select-all"
                    />
                  )}
                </TableHeader>
                <TableHeader>Row</TableHeader>
                <TableHeader>Provider</TableHeader>
                <TableHeader>Facility</TableHeader>
                <TableHeader>Shift</TableHeader>
                <TableHeader>Outcome</TableHeader>
                <TableHeader>Error</TableHeader>
                <TableHeader>Resolution</TableHeader>
                <TableHeader>Assignment</TableHeader>
                <TableHeader className="tw:w-8">
                  <span className="tw:sr-only">Raw data</span>
                </TableHeader>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <TableRow noPointer key={row.id} data-testid={`ingest-row-${row.id}`}>
                    <TableCell>
                      {!frozen && isActionableError(row) && (
                        <Checkbox
                          color="brandgreen"
                          checked={allOpenSelected || selectedIds.has(row.id)}
                          onChange={() => toggleRow(row)}
                          aria-label={`Select row ${row.row_number ?? ''}`}
                          data-testid={`ingest-row-select-${row.id}`}
                        />
                      )}
                    </TableCell>
                    <TableCell>{row.row_number ?? '—'}</TableCell>
                    <TableCell>{rowProviderName(row)}</TableCell>
                    <TableCell>
                      {row.facility_name || normValue(row, 'facility_name') || '—'}
                    </TableCell>
                    <TableCell>
                      <div>{rowShiftDate(row)}</div>
                      <div className="tw:text-xs tw:text-zinc-500">{rowShiftTimes(row)}</div>
                    </TableCell>
                    <TableCell>
                      <Badge color={getIngestRowOutcomeBadgeColor(row.outcome ?? '')}>
                        {labelMaps?.IngestRowOutcome?.[row.outcome ?? ''] || row.outcome}
                      </Badge>
                    </TableCell>
                    <TableCell className="tw:max-w-[320px]">
                      {row.error_code ? (
                        <>
                          <div className="tw:font-medium">
                            {labelMaps?.IngestRowErrorCode?.[row.error_code] || row.error_code}
                          </div>
                          <div className="tw:text-xs tw:text-zinc-500 tw:whitespace-normal">
                            {row.error_message}
                          </div>
                        </>
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell>
                      {row.resolution
                        ? labelMaps?.IngestRowResolution?.[row.resolution] || row.resolution
                        : '—'}
                    </TableCell>
                    <TableCell>
                      {row.assignment ? (
                        <Link
                          className="tw:text-brand-green-0 tw:underline"
                          to={`/staff/assignments/${row.assignment}`}
                        >
                          {row.assignment_code || 'View'}
                        </Link>
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell>
                      <Button
                        plain
                        small
                        onClick={() => setRawDataRow(row)}
                        title="View raw data as received"
                        aria-label={`View raw data for row ${row.row_number ?? ''}`}
                        data-testid={`ingest-row-raw-${row.id}`}
                      >
                        <i className="far fa-fw fa-code" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {hasNextPage && (
              <div ref={loadingRef} className="tw:flex tw:justify-center tw:py-8">
                {isFetchingNextPage && (
                  <div className="tw:text-center tw:text-zinc-500">Loading more rows...</div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      <MarkNotLoadedDialog
        open={showNotLoadedDialog}
        onClose={() => setShowNotLoadedDialog(false)}
        ingestFile={ingestFile}
        onChanged={invalidate}
      />

      <RawDataDialog row={rawDataRow} onClose={() => setRawDataRow(null)} />
    </main>
  );
};
